import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { SubTask, Task, TaskTypeAction } from '@k12/models';

interface IncomeVerificationHouseholdMember {
  fullName?: string | null;
  age?: number | null;
  relationshipToStudent?: string | null;
  hadIncome?: boolean | null;
  filedFederalTaxReturn?: boolean | null;
}

interface IncomeVerificationFormData {
  householdMembers?: IncomeVerificationHouseholdMember[];

  earningsFromEmployerNoTaxFiling?: boolean | null;
  oddJobsWithoutW2?: boolean | null;
  noIncome?: boolean | null;
  ownOrRentHome?: boolean | null;
  freeRoomAndMeals?: boolean | null;

  afdcTanf?: boolean | null;
  ssiDisability?: boolean | null;
  socialSecurityBenefits?: boolean | null;
  snap?: boolean | null;
  wic?: boolean | null;
  childSupportAlimony?: boolean | null;
  workersCompensationUnemployment?: boolean | null;
  militaryAllowances?: boolean | null;
  monetaryGiftsFromFamilyFriends?: boolean | null;
  miscellaneousResources?: boolean | null;

  certified?: boolean | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-income-verification-form',
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, MatCardModule, MatCheckboxModule, MatInputModule, MatRadioModule],
  templateUrl: './income-verification-form.component.html',
  styleUrl: './income-verification-form.component.scss'
})
export class IncomeVerificationFormComponent implements OnInit {
  @Input() task: Task | null;
  @Input() taskAction!: SubTask | null;
  @Input() taskTypeAction!: TaskTypeAction | null;
  @Input() isReadOnly: boolean = false;
  @Output() submitFormData = new EventEmitter<string>();

  private readonly fb = inject(FormBuilder);

  protected form!: FormGroup;
  protected context: IncomeVerificationFormData = {};

  protected readonly yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  protected readonly resourceQuestions = [
    { key: 'afdcTanf', label: 'AFDC/TANF' },
    { key: 'ssiDisability', label: 'SSI (Disability)' },
    { key: 'socialSecurityBenefits', label: 'Social Security Benefits' },
    { key: 'snap', label: 'SNAP' },
    { key: 'wic', label: 'WIC' },
    { key: 'childSupportAlimony', label: 'Child Support/Alimony' },
    { key: 'workersCompensationUnemployment', label: 'Worker’s Compensation/Unemployment' },
    { key: 'militaryAllowances', label: 'Military Allowances' },
    { key: 'monetaryGiftsFromFamilyFriends', label: 'Monetary Gifts from Family/Friends' },
    { key: 'miscellaneousResources', label: 'Miscellaneous Resources' }
  ];

  protected readonly taxYear = new Date().getFullYear() - 1;

  protected readonly financialQuestions = [
    {
      key: 'earningsFromEmployerNoTaxFiling',
      label: `I had earnings from an employer in ${this.taxYear} but am not required to file taxes.`
    },
    {
      key: 'oddJobsWithoutW2',
      label: 'My earnings are from odd jobs without a W2.'
    },
    {
      key: 'noIncome',
      label: `I did not work and had no income in ${this.taxYear}.`
    },
    {
      key: 'ownOrRentHome',
      label: 'Do you own or rent your home?'
    },
    {
      key: 'freeRoomAndMeals',
      label: `Did you live with someone who provided free room and meals in ${this.taxYear}?`
    }
  ];

  public ngOnInit(): void {
    const raw = this.taskAction?.formData ? this.safeParseJson(this.taskAction.formData) : {};
    this.context = this.normalizeKeys(raw) as IncomeVerificationFormData;

    this.form = this.fb.group({
      householdMembers: this.fb.array([]),

      earningsFromEmployerNoTaxFiling: [this.context.earningsFromEmployerNoTaxFiling ?? null, Validators.required],
      oddJobsWithoutW2: [this.context.oddJobsWithoutW2 ?? null, Validators.required],
      noIncome: [this.context.noIncome ?? null, Validators.required],
      ownOrRentHome: [this.context.ownOrRentHome ?? null, Validators.required],
      freeRoomAndMeals: [this.context.freeRoomAndMeals ?? null, Validators.required],

      afdcTanf: [this.context.afdcTanf ?? null, Validators.required],
      ssiDisability: [this.context.ssiDisability ?? null, Validators.required],
      socialSecurityBenefits: [this.context.socialSecurityBenefits ?? null, Validators.required],
      snap: [this.context.snap ?? null, Validators.required],
      wic: [this.context.wic ?? null, Validators.required],
      childSupportAlimony: [this.context.childSupportAlimony ?? null, Validators.required],
      workersCompensationUnemployment: [this.context.workersCompensationUnemployment ?? null, Validators.required],
      militaryAllowances: [this.context.militaryAllowances ?? null, Validators.required],
      monetaryGiftsFromFamilyFriends: [this.context.monetaryGiftsFromFamilyFriends ?? null, Validators.required],
      miscellaneousResources: [this.context.miscellaneousResources ?? null, Validators.required],

      certified: [this.context.certified ?? false, Validators.requiredTrue]
    });

    const members = this.context.householdMembers?.length ? this.context.householdMembers : [];
    members.forEach((member) => this.householdMembers.push(this.createHouseholdMemberGroup(member)));

    if (this.isReadOnly) {
      this.form.disable({ emitEvent: false });
    }
  }

  protected get householdMembers(): FormArray {
    return this.form.get('householdMembers') as FormArray;
  }

  protected addHouseholdMember(): void {
    this.householdMembers.push(this.createHouseholdMemberGroup());
    this.form.markAsDirty();
  }

  protected removeHouseholdMember(index: number): void {
    this.householdMembers.removeAt(index);
    this.form.markAsDirty();
  }

  protected isFormReady(): boolean {
    return this.form.valid && this.form.dirty && this.householdMembers.length > 0;
  }

  protected onBack(): void {
    globalThis.history.back();
  }

  protected submitForm(): void {
    if (!this.isFormReady()) return;

    this.submitFormData.emit(
      JSON.stringify({
        ...this.context,
        ...this.form.getRawValue()
      })
    );
  }

  private createHouseholdMemberGroup(member: IncomeVerificationHouseholdMember = {}): FormGroup {
    return this.fb.group({
      fullName: [member.fullName ?? '', Validators.required],
      age: [member.age ?? null, [Validators.required, Validators.min(0)]],
      relationshipToStudent: [member.relationshipToStudent ?? '', Validators.required],
      hadIncome: [member.hadIncome ?? null, Validators.required],
      filedFederalTaxReturn: [member.filedFederalTaxReturn ?? null, Validators.required]
    });
  }

  private safeParseJson(raw: string): Record<string, unknown> {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }

  private normalizeKeys(raw: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(raw).map(([key, value]) => [key.charAt(0).toLowerCase() + key.slice(1), value])
    );
  }
}
